import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useCart } from '../context/CartContext';
import {
  ChevronRight, Star, Shield, Truck, MessageCircle,
  ArrowRight, Check, MapPin, ChevronDown, Users, Calendar, Package
} from 'lucide-react';
import TrustBlockPremium from '../components/common/TrustBlockPremium';
import ReviewsWidget from '../components/common/ReviewsWidget';
import MobileStickyBar from '../components/common/MobileStickyBar';
import CallButton from '../components/common/CallButton';
import ContactModal from '../components/common/ContactModal';
import DispoUrgenceBlock from '../components/common/DispoUrgenceBlock';
import SEO from '../components/common/SEO';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const UNIVERS = [
  {
    id: 'anniversaire_enfant',
    label: 'Anniversaire enfant',
    desc: 'Châteaux gonflables, barbe à papa, mini golf, déco ballon…',
    image: 'https://images.unsplash.com/photo-1765947377925-941c12c84331?w=600&q=80',
    href: '/anniversaire-enfant-lot',
    badge: 'Très populaire',
    badgeBg: 'var(--rose-mist)',
    badgeColor: '#B94B5C'
  },
  {
    id: 'mariage',
    label: 'Mariage',
    desc: 'Photobooth, bars gourmands, coin enfants, décoration…',
    image: 'https://images.unsplash.com/photo-1728566831081-37200bc7c6d2?w=600&q=80',
    href: '/mariage-lot',
    badge: 'Premium',
    badgeBg: 'rgba(212,175,55,0.12)',
    badgeColor: '#7A5E00'
  },
  {
    id: 'entreprise',
    label: 'Entreprise & CE',
    desc: 'Cocktails, animations, teambuilding, tarifs CE avantageux…',
    image: 'https://images.unsplash.com/photo-1582183591295-9a2fe0170e3f?w=600&q=80',
    href: '/animations-entreprise-lot',
    badge: 'Tarifs CE',
    badgeBg: 'var(--teal-mist)',
    badgeColor: '#0B4B4E'
  },
  {
    id: 'jeux',
    label: 'Jeux & animations',
    desc: 'Jeux bois géants, blind test, parcours, mini golf…',
    image: 'https://images.unsplash.com/photo-1605173257854-303c2a5fb050?w=600&q=80',
    href: '/jeux-animations',
    badge: 'Nouveautés',
    badgeBg: 'rgba(93,188,191,0.12)',
    badgeColor: '#0B4B4E'
  },
  {
    id: 'gourmand',
    label: 'Animations gourmandes',
    desc: 'Barbe à papa, popcorn, crêpes, bar à bonbons…',
    image: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=600&q=80',
    href: '/animations-gourmandes',
    badge: 'Bestseller',
    badgeBg: 'rgba(212,175,55,0.12)',
    badgeColor: '#7A5E00'
  },
  {
    id: 'bapteme',
    label: 'Baptême & communion',
    desc: 'Bonbons, crêpes, décoration douce et élégante…',
    image: 'https://images.unsplash.com/photo-1768900318188-79f4291ad4a8?w=600&q=80',
    href: '/catalogue',
    badge: 'Tout inclus',
    badgeBg: 'var(--rose-mist)',
    badgeColor: '#B94B5C'
  },
];

const FAQ_DATA = [
  {
    q: "Quelle est votre zone d'intervention ?",
    a: "Nous intervenons dans un rayon de 1h30 autour de Figeac — couvrant le Lot (46), l'Aveyron (12), le Cantal (15) et la Corrèze (19). Vérifiez votre commune directement sur notre site."
  },
  {
    q: "Êtes-vous disponibles pendant toute la durée de l'événement ?",
    a: "C'est notre grande force : nous sommes joignables 24h/24 durant votre événement. En cas de problème, panne ou besoin urgent, nous intervenons dans les meilleurs délais — si nous ne sommes pas déjà en déplacement, nous arrivons sur place !"
  },
  {
    q: "Pouvez-vous récupérer le matériel la nuit après un événement ?",
    a: "Absolument. Après une soirée, un concert ou un événement tardif, nous pouvons organiser une reprise nocturne du matériel. Nous nous adaptons à vos contraintes d'horaires."
  },
  {
    q: "Proposez-vous une prestation de surveillance ou d'animation sur place ?",
    a: "Oui ! Nous proposons une prestation de contrôle, de surveillance et d'animation sur place. Notre équipe reste présente pendant votre événement pour gérer le matériel, animer et garantir le bon déroulement."
  },
  {
    q: "Comment réserver une animation ?",
    a: "Utilisez notre configurateur en ligne pour composer votre événement, obtenez un devis instantané, puis payez un acompte de 30% pour valider votre réservation."
  },
  {
    q: "Quel est le délai de réservation minimum ?",
    a: "Nous recommandons de réserver 2 à 4 semaines avant votre événement. Des créneaux de dernière minute sont parfois disponibles, n'hésitez pas à nous appeler."
  },
  {
    q: "Proposez-vous des tarifs pour les associations et CE ?",
    a: "Oui ! Nous proposons des tarifs préférentiels pour les associations, comités d'entreprise et groupes, jusqu'à 20% de remise."
  },
  {
    q: "Qui s'occupe de la livraison et de l'installation ?",
    a: "Notre équipe prend en charge la livraison, l'installation et la reprise de tout le matériel. Vous n'avez rien à faire sauf profiter !"
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl cursor-pointer overflow-hidden"
      style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 4px 16px rgba(26,26,46,0.05)' }}
    >
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-semibold text-sm sm:text-base" style={{ color: 'var(--ink)' }}>{q}</span>
        <ChevronDown
          size={18}
          style={{
            color: 'var(--teal)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease'
          }}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    fetch(`${BACKEND}/api/products?featured=true&limit=6`)
      .then(r => r.json())
      .then(d => setFeaturedProducts(d.products || []))
      .catch((err) => { /* eslint-disable-line no-empty */ });

    fetch(`${BACKEND}/api/avis?public=true&limit=5`)
      .then(r => r.json())
      .then(d => setReviews(d.reviews || []))
      .catch((err) => { /* eslint-disable-line no-empty */ });
  }, []);

  return (
    <div style={{ background: 'var(--bg, #FBF7F1)', minHeight: '100vh' }}>
      <Header />
      <SEO
        title="Location matériel événementiel — Figeac, Lot (46)"
        description="Spécialiste location animations événementielles à Figeac. Photobooth, jeux gonflables, animations gourmandes. Livraison Lot (46), Aveyron, Cantal. Devis gratuit."
        canonical="https://show-evenement.com/"
      />

      {/* ================================================================
          BLOC 1 — HÉRO (version originale validée)
          ================================================================ */}
      <section
        data-testid="home-hero"
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #FBF7F1 0%, #E8F5F5 40%, #FFF0F3 100%)',
          paddingTop: '3.5rem',
          paddingBottom: '3.5rem',
          minHeight: '85vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Points décoratifs */}
        <div className="absolute" style={{ top: '15%', left: '8%', width: 10, height: 10, borderRadius: '50%', background: 'var(--teal)', opacity: 0.18 }} />
        <div className="absolute" style={{ top: '40%', left: '4%', width: 7, height: 7, borderRadius: '50%', background: 'var(--rose)', opacity: 0.22 }} />
        <div className="absolute" style={{ top: '20%', right: '6%', width: 12, height: 12, borderRadius: '50%', background: '#7DB1FF', opacity: 0.15 }} />
        <div className="absolute" style={{ bottom: '25%', right: '10%', width: 8, height: 8, borderRadius: '50%', background: 'var(--teal)', opacity: 0.2 }} />
        <div className="absolute" style={{ bottom: '15%', left: '12%', width: 6, height: 6, borderRadius: '50%', background: 'var(--rose)', opacity: 0.18 }} />

        <div className="container-ose w-full">
          <div className="max-w-3xl mx-auto text-center">

            {/* Badge spécialiste */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-5"
              style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 2px 12px rgba(26,26,46,0.07)' }}>
              <Star size={14} fill="var(--gold)" color="var(--gold)" />
              <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                Spécialiste Figeac · Lot · Aveyron · Cantal
              </span>
            </div>

            {/* Étoiles + avis */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map(s => <Star key={s} size={18} fill="var(--gold)" color="var(--gold)" />)}
              </div>
              <span className="font-bold text-base" style={{ color: 'var(--gold)' }}>4,6</span>
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>— 9 avis clients</span>
            </div>

            {/* Titre principal — 4 lignes multi-couleurs */}
            <h1 className="font-display font-bold mb-6 text-center" style={{ lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              <span style={{ display: 'block', fontSize: 'clamp(2.4rem, 7vw, 4.2rem)', color: 'var(--ink)' }}>
                Créez votre événement
              </span>
              <span style={{ display: 'block', fontSize: 'clamp(2rem, 5.5vw, 3.2rem)', color: 'var(--teal)' }}>
                simplement.
              </span>
              <span style={{ display: 'block', fontSize: 'clamp(2.2rem, 6.5vw, 4rem)', color: 'var(--ink)' }}>
                Réservez. Composez.
              </span>
              <span style={{ display: 'block', fontSize: 'clamp(2rem, 6vw, 3.8rem)', color: 'var(--rose, #E8637A)' }}>
                Profitez.
              </span>
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg mb-8 mx-auto"
              style={{ color: 'var(--text-muted)', lineHeight: '1.7', maxWidth: 560 }}>
              Location de matériel événementiel, animations gourmandes,
              jeux et décorations. Livraison dans un rayon de 1h30 autour
              de Figeac — Lot, Aveyron, Cantal, Corrèze.
            </p>

            {/* Badge livraison offerte */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: 'rgba(31,157,122,0.08)', border: '1px solid rgba(31,157,122,0.3)' }}>
              <Truck size={15} style={{ color: '#1F9D7A' }} />
              <span className="text-sm font-bold" style={{ color: '#1F9D7A' }}>
                🎁 Livraison offerte jusqu'à 20 km autour de Figeac
              </span>
            </div>

            {/* CTA — créer un événement + louer du matériel */}
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/creer-evenement"
                data-testid="home-hero-primary-cta"
                className="btn-teal inline-flex items-center justify-center gap-3 px-10 py-4 text-lg font-bold rounded-2xl"
                style={{
                  boxShadow: '0 8px 28px rgba(93,188,191,0.35)',
                  minWidth: 260,
                }}
              >
                Créer mon événement <ArrowRight size={20} />
              </Link>
              <Link
                to="/catalogue"
                data-testid="home-hero-rent-cta"
                className="inline-flex items-center justify-center gap-3 px-10 py-4 text-lg font-bold rounded-2xl transition-colors"
                style={{
                  background: '#FFFFFF',
                  color: '#0B4B4E',
                  border: '2px solid var(--teal)',
                  minWidth: 240,
                }}
              >
                <Package size={20} /> Louer du matériel
              </Link>
            </div>

          </div>
        </div>
      </section>

      <div className="divider-gold" style={{ opacity: 0.4 }} />

      {/* ================================================================
          BLOC 2 — ACCÈS RAPIDE (remonté en 1ère position après héro)
          ================================================================ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2"
              style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Que souhaitez-vous faire ?
            </h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Louer, acheter, créer ou trouver un prestataire</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { href: '/catalogue', icon: '🎪', label: 'Louer une animation', desc: '53 produits disponibles', color: 'var(--teal)', bg: 'var(--teal-mist)' },
              { href: '/boutique', icon: '🛍️', label: 'Acheter en boutique', desc: 'Déco, accessoires, cadeaux', color: '#7A5E00', bg: 'rgba(212,175,55,0.08)' },
              { href: '/fabrication', icon: '✨', label: 'Créer sur-mesure', desc: 'Vous imaginez, nous fabriquons', color: '#B94B5C', bg: 'var(--rose-mist)' },
              { href: '/partenaires', icon: '🤝', label: 'Partenaires & Pro', desc: 'Traiteurs, DJ, photographes...', color: '#0B4B4E', bg: 'var(--teal-mist)' },
            ].map((item, i) => (
              <Link key={item.href || `nav-${i}`} to={item.href}
                className="card-premium p-5 text-center group transition-all hover:shadow-md">
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="font-semibold text-sm mb-1" style={{ color: 'var(--ink)' }}>{item.label}</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="divider-gold" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 3 — FABRICATION SUR-MESURE (remonté après accès rapide)
          ================================================================ */}
      <section className="section-py section-cream">
        <div className="container-ose">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-4"
                style={{ background: 'var(--rose-mist)', color: '#B94B5C', border: '1px solid rgba(244,167,178,0.3)' }}>
                ✨ Création sur-mesure
              </span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4"
                style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                Vous imaginez<br />
                <span className="text-teal-gradient">nous fabriquons</span>
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)', lineHeight: '1.8' }}>
                Arche ballons sur-mesure aux couleurs de votre mariage,
                panneau bienvenue avec vos prénoms et date,
                décoration thémée entièrement personnalisée.
                Partagez votre idée, nous la réalisons.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { icon: '🎈', label: 'Arche ballons', sub: 'Palette au choix' },
                  { icon: '🎨', label: 'Panneau personnalisé', sub: 'Bois, acrylique' },
                  { icon: '✨', label: 'Déco thémée', sub: 'Sur-mesure complet' },
                  { icon: '📷', label: 'Cadre photo', sub: 'Pour photobooth' },
                ].map((f, i) => (
                  <div key={`fab-${i}`} className="flex items-center gap-2.5 p-3 rounded-xl"
                    style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                    <span className="text-xl">{f.icon}</span>
                    <div>
                      <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>{f.label}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{f.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/fabrication"
                className="btn-teal inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-base font-bold">
                Faire une demande sur-mesure <ArrowRight size={18} />
              </Link>
              <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                Réponse sous 48h · Devis gratuit
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758606137571-79fb4dd7d5a4?w=600&q=80"
                alt="Fabrication sur-mesure arche ballons"
                className="w-full h-72 object-cover rounded-3xl"
                style={{ boxShadow: '0 20px 60px rgba(244,167,178,0.2)' }}
                loading="lazy" />
              <div className="absolute -bottom-4 -left-4 p-4 rounded-2xl"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(26,26,46,0.1)' }}>
                <p className="text-sm font-bold" style={{ color: 'var(--ink)' }}>✅ Livraison incluse</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Installation à votre lieu</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-teal" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 4 — CONFIGURATEUR INTELLIGENT + PLAN DE TABLE
          ================================================================ */}
      <section className="section-py" style={{ background: '#0B1E20' }}>
        <div className="container-ose">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Colonne gauche — Configurateur */}
            <div>
              <span className="inline-block px-4 py-2 rounded-full text-xs font-bold mb-5"
                style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }}>
                ✨ Bien plus qu'une location
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4"
                style={{ color: '#FFFFFF', letterSpacing: '-0.03em', lineHeight: '1.1' }}>
                Un vrai organisateur<br />
                <span style={{ color: '#D4AF37' }}>événementiel intelligent</span>
              </h2>
              <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.65)', lineHeight: '1.8' }}>
                Show Événement, c'est l'accompagnement complet de A à Z :
                guides, checklists, budget, planning, prestataires recommandés,
                invités et suivi jusqu'au Jour J.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { icon: '📋', label: 'Checklists interactives', desc: 'Ne rien oublier — par type d\'événement' },
                  { icon: '💰', label: 'Tableau de budget', desc: 'Prévu, réel, payé, restant — en temps réel' },
                  { icon: '👥', label: 'Gestion des invités & RSVP', desc: 'Invitations, allergies, tablage automatique' },
                  { icon: '🤝', label: 'Prestataires recommandés', desc: 'Photographe, DJ, traiteur — sélectionnés par nos soins' },
                  { icon: '📅', label: 'Planning Jour J', desc: 'Déroulé heure par heure, responsables, statuts' },
                  { icon: '🚀', label: 'Disponibles 24h/24', desc: 'Intervention garantie pendant votre événement' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-base flex-shrink-0">{item.icon}</span>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>{item.label}</p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/creer-evenement"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm"
                  style={{ background: '#D4AF37', color: '#0B1E20' }}>
                  Créer mon événement <ArrowRight size={16} />
                </Link>
                <Link to="/connexion"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm"
                  style={{ border: '1px solid rgba(255,255,255,0.2)', color: '#FFFFFF' }}>
                  Créer mon espace gratuit
                </Link>
              </div>
            </div>
            {/* Colonne droite — Guides */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { href: '/guide/anniversaire-adulte', emoji: '🎉', title: 'Anniversaire adulte', desc: '12 thèmes, checklist, budget, planning' },
                { href: '/mariage-lot', emoji: '💍', title: 'Mariage', desc: 'Guide complet Jour J + prestataires' },
                { href: '/anniversaire-enfant-lot', emoji: '🎈', title: 'Anniversaire enfant', desc: 'Animations sûres, thèmes, checklist' },
                { href: '/animations-entreprise-lot', emoji: '💼', title: 'Entreprise & CE', desc: 'Budget pro, facturation, animation' },
                { href: '/prestataires-recommandes', emoji: '📸', title: 'Prestataires', desc: 'Photographes, DJ, traiteurs recommandés' },
                { href: '/guides', emoji: '📚', title: 'Tous les guides', desc: 'Voir toutes les ressources' },
              ].map((item, i) => (
                <Link key={i} to={item.href}
                  className="p-4 rounded-2xl hover:scale-105 transition-transform duration-200 block"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="text-2xl mb-2">{item.emoji}</div>
                  <p className="font-bold text-sm mb-1" style={{ color: '#FFFFFF' }}>{item.title}</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', lineHeight: '1.4' }}>{item.desc}</p>
                </Link>
              ))}
            </div>
          </div>

          {/* ── MODULE PLAN DE TABLE ── */}
          <div className="rounded-3xl overflow-hidden"
            style={{ border: '1px solid rgba(212,175,55,0.25)', background: 'rgba(255,255,255,0.03)' }}>
            <div className="grid lg:grid-cols-2 gap-0 items-center">
              {/* Visuel plan de table */}
              <div className="relative overflow-hidden" style={{ minHeight: 280 }}>
                <img
                  src="https://images.unsplash.com/photo-1519167758481-83f29c8a4f6a?w=700&q=80"
                  alt="Plan de table interactif Show Événement"
                  className="w-full h-full object-cover"
                  style={{ minHeight: 280 }}
                  loading="lazy"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(11,30,32,0.7) 0%, transparent 70%)' }} />
                {/* Badge sur la photo */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  <span className="px-3 py-1.5 rounded-full text-xs font-bold"
                    style={{ background: 'rgba(212,175,55,0.9)', color: '#0B1E20' }}>
                    🪑 Plan de table interactif
                  </span>
                  <span className="px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ background: 'rgba(93,188,191,0.9)', color: '#0B1E20' }}>
                    ✓ Inclus avec votre réservation
                  </span>
                </div>
                {/* Mini stats visuelles */}
                <div className="absolute bottom-4 left-4 flex gap-2">
                  {[
                    { val: '15m×10m', label: 'Salle réelle' },
                    { val: 'Drag & Drop', label: 'Tables' },
                    { val: 'QR Code', label: 'Partage' },
                  ].map((s, i) => (
                    <div key={i} className="text-center px-2.5 py-1.5 rounded-xl"
                      style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}>
                      <p className="text-xs font-bold" style={{ color: '#FFFFFF' }}>{s.val}</p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Texte plan de table */}
              <div className="p-8 sm:p-10">
                <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-4"
                  style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37' }}>
                  Nouveau
                </span>
                <h3 className="font-display text-2xl font-bold mb-3"
                  style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
                  Votre plan de table<br />
                  <span style={{ color: '#D4AF37' }}>en vraies dimensions</span>
                </h3>
                <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.7' }}>
                  Dessinez votre salle à l'échelle réelle (ex: 15m × 10m),
                  placez vos tables en glisser-déposer, assignez vos invités
                  et partagez le plan via QR code.
                </p>
                <div className="space-y-2.5 mb-7">
                  {[
                    '🗺️ Salle à l\'échelle réelle en mètres',
                    '🪑 Tables ronde Ø 1.5m, rectangulaire 1.8×0.75m...',
                    '👥 Gestion invités + RSVP en ligne',
                    '📱 QR code de partage pour vos invités',
                    '🖨️ Export PDF plan + cartons de table',
                    '✉️ Invitations personnalisées par IA',
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check size={13} style={{ color: '#D4AF37', flexShrink: 0 }} />
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>{f}</span>
                    </div>
                  ))}
                </div>
                <Link to="/mon-organisation"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm"
                  style={{ background: '#D4AF37', color: '#0B1E20' }}>
                  <Users size={15} /> Accéder à mon plan de table
                </Link>
                <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Gratuit · Inclus avec toute réservation Show Événement
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-gold" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 5 — DISPONIBILITÉS EN TEMPS RÉEL
          ================================================================ */}
      <DispoUrgenceBlock />

      <div className="divider-teal" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 6 — ZONES DESSERVIES (remontées après disponibilités)
          ================================================================ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div
            className="rounded-3xl p-8 md:p-12"
            style={{
              background: 'linear-gradient(135deg, var(--teal-mist) 0%, #FFFFFF 100%)',
              border: '1px solid rgba(93,188,191,0.25)'
            }}
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
                  style={{ background: 'var(--teal-mist)', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.3)' }}>
                  Zone d'intervention
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4"
                  style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                  Dans un rayon de 1h30 autour de Figeac
                </h2>
                <p className="text-sm mb-6" style={{ color: 'var(--text-muted)', lineHeight: '1.75' }}>
                  Basés à Figeac, nous intervenons dans un rayon de 1h30 — Lot (46), Aveyron (12), Cantal (15) et Corrèze (19).
                </p>
                {/* Badge livraison offerte */}
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl mb-4"
                  style={{ background: 'rgba(31,157,122,0.08)', border: '1px solid rgba(31,157,122,0.25)', display: 'inline-flex' }}>
                  <Truck size={15} style={{ color: '#1F9D7A' }} />
                  <span className="text-sm font-bold" style={{ color: '#1F9D7A' }}>
                    🎁 Livraison offerte jusqu'à 20 km autour de Figeac
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Figeac', 'Cahors', 'Rodez', 'Aurillac', 'Saint-Céré', 'Decazeville', 'Gourdon', 'Souillac', 'Villefranche', 'Millau'].map(z => (
                    <span key={z} className="px-3 py-1.5 rounded-xl text-xs font-medium"
                      style={{ background: '#FFFFFF', color: 'var(--ink)', border: '1px solid var(--border)' }}>
                      <MapPin size={9} className="inline mr-1" style={{ color: 'var(--teal)' }} />{z}
                    </span>
                  ))}
                </div>
                <Link to="/contact" className="btn-teal inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl">
                  <MapPin size={15} /> Vérifier ma zone
                </Link>
              </div>
              <div className="text-center">
                <div className="inline-flex flex-col items-center gap-4 p-8 rounded-2xl"
                  style={{ background: '#FFFFFF', border: '1px solid rgba(93,188,191,0.2)', boxShadow: '0 8px 32px rgba(93,188,191,0.1)' }}>
                  <div className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--teal-mist)' }}>
                    <MapPin size={36} style={{ color: 'var(--teal)' }} />
                  </div>
                  <p className="font-display text-2xl font-bold" style={{ color: 'var(--ink)' }}>Lot · Aveyron · Cantal</p>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Rayon 1h30 autour de Figeac</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-gold" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 7 — PACKS INTELLIGENTS (avant les produits phares)
          ================================================================ */}
      <section className="section-py section-cream">
        <div className="container-ose">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
              style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
              Packs intelligents
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2"
              style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Composez en 1 clic
            </h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Nos packs pré-configurés avec remise exclusive
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { href: '/packs', label: 'Mariage Premium', badge: '-10%', img: 'https://images.unsplash.com/photo-1728566831081-37200bc7c6d2?w=300&q=70' },
              { href: '/packs', label: 'Anniversaire Enfant', badge: '-8%', img: 'https://images.unsplash.com/photo-1765947377925-941c12c84331?w=300&q=70' },
              { href: '/packs', label: 'Soirée Conviviale', badge: '-8%', img: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=300&q=70' },
              { href: '/packs', label: 'Cocktail Entreprise', badge: '-12%', img: 'https://images.unsplash.com/photo-1631901589746-219fcffbdd29?w=300&q=70' },
            ].map((p, i) => (
              <Link key={`pack-${i}`} to={p.href} className="card-premium overflow-hidden group block">
                <div className="relative overflow-hidden">
                  <img src={p.img} alt={p.label}
                    className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy" />
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{ background: '#1A1A2E', color: 'var(--gold)' }}>{p.badge}</span>
                </div>
                <div className="p-3">
                  <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>{p.label}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/packs" className="btn-teal px-6 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
              Voir tous les packs <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <div className="divider-gold" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 8 — PRODUITS PHARES
          ================================================================ */}
      {featuredProducts.length > 0 && (
        <section className="section-py" style={{ background: 'var(--teal-mist, #E7F6F6)' }}>
          <div className="container-ose">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3"
                  style={{ background: '#FFFFFF', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.3)' }}>
                  Bestsellers
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-bold"
                  style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                  Nos animations phares
                </h2>
                <p className="mt-1.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                  Les plus réservées — Lot, Aveyron, Cantal
                </p>
              </div>
              <Link to="/catalogue" className="flex items-center gap-1 text-sm font-semibold"
                style={{ color: 'var(--teal)' }}>
                Tout voir <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredProducts.slice(0, 6).map(p => (
                <div key={p.id} className="card-premium overflow-hidden" style={{ background: '#FFFFFF' }}>
                  <Link to={`/produit/${p.id}`}>
                    <img
                      src={p.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=70'}
                      alt={p.nom}
                      className="w-full h-44 object-cover transition-transform duration-300 hover:scale-105"
                      loading="lazy" />
                  </Link>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Link to={`/produit/${p.id}`}>
                        <h3 className="font-semibold text-sm leading-snug" style={{ color: 'var(--ink)' }}>{p.nom}</h3>
                      </Link>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0"
                        style={{
                          background: p.type === 'location' ? 'var(--teal-mist)' : p.type === 'vente' ? 'rgba(212,175,55,0.12)' : 'var(--rose-mist)',
                          color: p.type === 'location' ? '#0B4B4E' : p.type === 'vente' ? '#7A5E00' : '#B94B5C'
                        }}>
                        {p.type === 'location' ? 'Location' : p.type === 'vente' ? 'Vente' : 'Fabrication'}
                      </span>
                    </div>
                    <p className="text-xs mb-4 line-clamp-2" style={{ color: 'var(--text-muted)' }}>{p.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="price-gold text-base">à partir de {p.tarifs?.particulier || 0}€</span>
                      <button onClick={() => addItem(p)} className="btn-teal px-3 py-1.5 text-xs font-semibold rounded-lg">
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="divider-gold" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 9 — POURQUOI NOUS CHOISIR
          ================================================================ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <TrustBlockPremium />
        </div>
      </section>

      <div className="divider-teal" style={{ opacity: 0.5 }} />

      {/* ================================================================
          BLOC 10 — AVIS CLIENTS
          ================================================================ */}
      {reviews.length > 0 && (
        <section className="section-py" style={{ background: 'var(--rose-mist, #FFF1F4)' }}>
          <div className="container-ose">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
                style={{ background: '#FFFFFF', color: '#B94B5C', border: '1px solid rgba(244,167,178,0.4)' }}>
                Avis clients vérifiés
              </span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3"
                style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                Ce que disent nos clients
              </h2>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(s => <Star key={s} size={17} fill="var(--gold)" color="var(--gold)" />)}
                  <span className="ml-1 text-sm font-bold" style={{ color: 'var(--gold)' }}>4,6</span>
                </div>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>— Note Google sur</span>
                <a href="https://share.google/hZSHglw36RKeQgqnp" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: '#FFFFFF', border: '1px solid rgba(26,26,46,0.12)', color: '#1A1A2E' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google
                </a>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {reviews.slice(0, 6).map(r => (
                <div key={r.id} className="card-premium p-5" style={{ background: '#FFFFFF' }}>
                  <div className="flex items-center gap-1 mb-3">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={13} fill={s <= r.note ? 'var(--gold)' : 'transparent'} color="var(--gold)" />
                    ))}
                  </div>
                  <p className="text-sm mb-3 italic" style={{ color: 'var(--text-muted)', lineHeight: '1.65' }}>
                    "{r.commentaire}"
                  </p>
                  <div className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>— {r.client_nom}</div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <a href="https://share.google/hZSHglw36RKeQgqnp" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold"
                style={{ background: 'var(--teal)', color: '#0B1E20', textDecoration: 'none', boxShadow: '0 4px 14px rgba(93,188,191,0.3)' }}>
                <Star size={15} fill="#0B1E20" color="#0B1E20" />
                Laissez votre avis Google
              </a>
              <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                Votre avis aide d'autres familles à nous faire confiance ❤️
              </p>
            </div>
          </div>
        </section>
      )}

      <div className="divider-gold" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 11 — FAQ
          ================================================================ */}
      <section className="section-py section-cream">
        <div className="container-ose">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3"
              style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Questions fréquentes
            </h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Tout ce qu'il faut savoir avant de réserver
            </p>
          </div>
          <div className="max-w-2xl mx-auto flex flex-col gap-3">
            {FAQ_DATA.map((f, i) => <FAQItem key={`faq-${i}`} {...f} />)}
          </div>
        </div>
      </section>

      <div className="divider-teal" style={{ opacity: 0.3 }} />

      {/* ================================================================
          BLOC 12 — NOS UNIVERS (descendu avant CTA)
          ================================================================ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold mb-4"
              style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
              Nos univers événementiels
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3"
              style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Pour chaque moment de vie
            </h2>
            <p className="text-base max-w-lg mx-auto" style={{ color: 'var(--text-muted)' }}>
              Particuliers, entreprises ou commerces — Show Événement s'adapte à votre contexte.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {UNIVERS.map((u) => (
              <Link key={u.id} to={u.href} data-testid="universe-card"
                className="card-premium overflow-hidden group block">
                <div className="relative overflow-hidden">
                  <img src={u.image} alt={u.label}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy" />
                  <div className="absolute inset-0"
                    style={{ background: 'linear-gradient(0deg, rgba(26,26,46,0.35) 0%, transparent 55%)' }} />
                  <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold"
                    style={{ background: u.badgeBg, color: u.badgeColor }}>
                    {u.badge}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-lg mb-1" style={{ color: 'var(--ink)' }}>{u.label}</h3>
                  <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>{u.desc}</p>
                  <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--teal)' }}>
                    Découvrir <ChevronRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Entreprises */}
          <div className="mt-10 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-2"
                  style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                  💼 Entreprises & Commerces
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-bold"
                  style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                  Vous êtes professionnel ?
                </h3>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                  Cocktails, animations commerciales, lancements produit, soirées VIP, team building.
                </p>
              </div>
              <Link to="/animations-entreprise-lot"
                className="flex items-center gap-1.5 text-sm font-semibold"
                style={{ color: 'var(--teal)' }}>
                Voir offres entreprise <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { emoji: '🚀', label: 'Lancement produit', href: '/creer-evenement' },
                { emoji: '📣', label: 'Animation commerciale', href: '/animation-commerciale-lot' },
                { emoji: '🤝', label: 'Team building', href: '/creer-evenement' },
                { emoji: '🥂', label: 'Cocktail entreprise', href: '/creer-evenement' },
                { emoji: '🚪', label: 'Portes ouvertes', href: '/creer-evenement' },
              ].map((e, i) => (
                <Link key={`ent-${i}`} to={e.href}
                  className="p-4 rounded-2xl text-center group transition-all"
                  style={{ background: '#FFFFFF', border: '1.5px solid var(--border)', textDecoration: 'none' }}
                  onMouseEnter={ev => ev.currentTarget.style.borderColor = 'var(--teal)'}
                  onMouseLeave={ev => ev.currentTarget.style.borderColor = 'var(--border)'}>
                  <div className="text-2xl mb-2">{e.emoji}</div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>{e.label}</p>
                </Link>
              ))}
            </div>
            <div className="mt-4 p-4 rounded-2xl flex flex-wrap items-center justify-between gap-3"
              style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
              <div>
                <p className="text-sm font-semibold" style={{ color: '#0B4B4E' }}>
                  Tarifs CE & Association disponibles
                </p>
                <p className="text-xs" style={{ color: 'rgba(11,75,78,0.7)' }}>
                  Jusqu'à -20% selon votre profil · Facturation professionnelle avec TVA
                </p>
              </div>
              <Link to="/creer-evenement"
                className="btn-teal px-5 py-2.5 text-sm font-bold rounded-xl flex-shrink-0">
                Créer un événement pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-gold" style={{ opacity: 0.4 }} />

      {/* ================================================================
          BLOC 13 — CTA FINAL
          ================================================================ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div
            className="relative overflow-hidden rounded-3xl text-center p-10 sm:p-14 noise-overlay"
            style={{
              background: 'linear-gradient(135deg, #5DBCBF 0%, #4FAEB1 100%)',
              boxShadow: '0 30px 80px rgba(93,188,191,0.25)'
            }}
          >
            <div className="absolute top-6 left-8 w-4 h-4 rounded-full opacity-25 bg-white" />
            <div className="absolute bottom-8 right-12 w-3 h-3 rounded-full opacity-20 bg-white" />
            <div className="absolute top-12 right-10 w-2 h-2 rounded-full opacity-30"
              style={{ background: 'var(--rose)' }} />
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4"
              style={{ color: '#0B1E20', letterSpacing: '-0.02em' }}>
              Prêt à créer votre événement ?
            </h2>
            <p className="text-base mb-8 max-w-xl mx-auto" style={{ color: 'rgba(11,30,32,0.65)' }}>
              Composez votre événement en 2 minutes, obtenez votre devis instantanément.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/creer-evenement"
                className="inline-flex items-center gap-2 justify-center px-9 py-4 text-base font-bold rounded-xl"
                style={{ background: '#0B1E20', color: '#FFFFFF', boxShadow: '0 10px 30px rgba(11,30,32,0.25)' }}>
                Créer mon événement <ArrowRight size={18} />
              </Link>
              <button
                onClick={() => setContactOpen(true)}
                className="btn-rose inline-flex items-center gap-2 justify-center px-9 py-4 text-base font-bold rounded-xl"
                style={{ color: 'var(--ink)', cursor: 'pointer', border: 'none' }}>
                <MessageCircle size={17} /> Nous contacter
              </button>
            </div>
          </div>
        </div>
      </section>

      <MobileStickyBar />
      <Footer />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} sourcePage="Accueil" />
    </div>
  );
}
